import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  TrendingUp,
  Plus,
  PieChart,
  BookOpen,
  Bot,
  ChevronRight,
  Zap,
  Crown,
  LogOut,
  AlertCircle,
  Lock,
  Newspaper,
  Sparkles,
  Compass,
  Trophy,
  LayoutDashboard,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  Award
} from 'lucide-react';
import Logo from './Logo';
import { usePortfolio } from '../context/PortfolioContext';
import { useCexAccount } from '../context/CexAccountContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../context/SidebarContext';

interface SidebarProps {
  plan?: string;
  onNewTradeClick?: () => void;
}

const PLAN_CONFIG = {
  expired: {
    label: 'Expired',
    color: 'text-red-500',
    bg: 'bg-red-950/20',
    border: 'border-red-900/40',
    icon: AlertCircle,
  },
  free: {
    label: 'Free',
    color: 'text-neutral-400',
    bg: 'bg-neutral-800/50',
    border: 'border-neutral-700',
    icon: null as any,
  },
  pro: {
    label: 'Pro',
    color: 'text-amber-400',
    bg: 'bg-amber-900/20',
    border: 'border-amber-700/40',
    icon: Zap,
  },
  premium: {
    label: 'Premium',
    color: 'text-purple-400',
    bg: 'bg-purple-900/20',
    border: 'border-purple-700/40',
    icon: Crown,
  },
};

const Sidebar: React.FC<SidebarProps> = () => {
  const navigate = useNavigate();
  const { logout } = usePortfolio();
  const { setShowManualWizard, setShowDexWizard } = useCexAccount();
  const { effectivePlan, subscription } = useSubscription();
  const { t } = useTranslation();
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();

  const currentPath = window.location.pathname.replace(/\/$/, '') || '/';

  const planCfg = PLAN_CONFIG[effectivePlan] ?? PLAN_CONFIG.free;
  const PlanIcon = planCfg.icon;
  const isPremium = effectivePlan === 'premium';
  const isTrial = subscription?.status === 'trial';

  const handleNewTrade = () => {
    setShowManualWizard(true);
    setIsMobileOpen(false);
  };

  const handleNewDexTrade = () => {
    setShowDexWizard(true);
    setIsMobileOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMobileOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-[60] w-10 h-10 bg-black border border-neutral-800 rounded-xl flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
      >
        <Menu size={20} />
      </button>

      {isMobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-[9993] bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMobileOpen(false)}
          />
          <div data-lenis-prevent className="md:hidden fixed inset-y-0 left-0 z-[9995] w-72 bg-black border-r border-neutral-900 overflow-y-auto">
            <div className="flex flex-col min-h-full p-6">
              <div className="flex items-center justify-between mb-6">
                <Logo size="lg" centered={false} />
                <button
                  onClick={() => setIsMobileOpen(false)}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleNewTrade}
                  className="flex-1 py-2 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] bg-red-600 hover:bg-red-700 shadow-red-900/20 text-sm"
                >
                  <Plus size={16} />
                  CEX
                </button>
                <button
                  onClick={handleNewDexTrade}
                  className="flex-1 py-2 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] bg-purple-600 hover:bg-purple-700 shadow-purple-900/20 text-sm"
                >
                  <Plus size={16} />
                  DEX
                </button>
              </div>

              <nav className="space-y-0.5">
                <div className="pt-2 pb-1 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
                  <LayoutDashboard size={10} className="text-neutral-400" /> Main Features
                </div>
                <NavItem icon={<BarChart size={16} />} label={t('sidebar.dashboard')} onClick={() => handleNavigation('/dashboard')} active={currentPath === '/dashboard'} locked={effectivePlan === 'expired'} isCollapsed={false} />
                <NavItem icon={<PieChart size={16} />} label={t('sidebar.reports')} onClick={() => handleNavigation('/reports')} active={currentPath === '/reports'} locked={effectivePlan === 'expired'} isCollapsed={false} />
                <NavItem icon={<TrendingUp size={16} />} label={t('sidebar.trades')} onClick={() => handleNavigation('/trades')} active={currentPath === '/trades'} locked={effectivePlan === 'expired'} isCollapsed={false} />
                <NavItem icon={<BookOpen size={16} />} label={t('sidebar.notebook')} onClick={() => handleNavigation('/notebook')} active={currentPath === '/notebook'} locked={effectivePlan === 'expired'} isCollapsed={false} />
                <NavItem icon={<Bot size={16} />} label={t('sidebar.aiAssistant')} onClick={() => handleNavigation('/ai-assistant')} active={currentPath === '/ai-assistant'} isCollapsed={false} />
                <NavItem icon={<Users size={16} />} label="Referral" onClick={() => handleNavigation('/referral')} active={currentPath === '/referral'} isCollapsed={false} />

                <div className="pt-2 pb-0.5 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
                  <Crown size={10} className="text-purple-400" /> Premium Features
                </div>
                <NavItem icon={<Newspaper size={16} />} label="Crypto News" onClick={() => handleNavigation('/news')} active={currentPath === '/news'} isCollapsed={false} />
                <NavItem icon={<Sparkles size={16} />} label="Dev Picks" onClick={() => handleNavigation('/dev-picks')} active={currentPath === '/dev-picks'} isCollapsed={false} />
                <NavItem icon={<Compass size={16} />} label="Top 50 Trending" onClick={() => handleNavigation('/trending')} active={currentPath === '/trending'} isCollapsed={false} />
                <NavItem icon={<Trophy size={16} />} label="Top Traders" onClick={() => handleNavigation('/top-traders')} active={currentPath === '/top-traders'} isCollapsed={false} />

                <div className="py-2">
                  <div className="h-px bg-neutral-900 w-full mb-2" />
                  <NavItem icon={<LogOut size={16} />} label={t('sidebar.logout')} onClick={handleLogout} isCollapsed={false} />
                </div>
              </nav>

              <div className="mt-auto pt-4 space-y-2">
              <div className={`flex items-center gap-2 px-2 py-1.5 rounded-xl border ${planCfg.bg} ${planCfg.border}`}>
                {PlanIcon && <PlanIcon size={12} className={planCfg.color} />}
                <div className="flex-1 min-w-0">
                  <p className={`text-[10px] font-black uppercase tracking-widest ${planCfg.color}`}>
                    {planCfg.label} Plan
                  </p>
                  {isTrial && subscription?.trial_ends_at && (
                    <p className="text-[9px] text-neutral-500 truncate">
                      Trial sampai {new Date(subscription.trial_ends_at).toLocaleDateString('id-ID')}
                    </p>
                  )}
                </div>
              </div>

              {!isPremium && (
                <div
                  onClick={() => { handleNavigation('/pricing'); }}
                  className="bg-gradient-to-br from-neutral-900 to-neutral-950 p-3 rounded-3xl border border-neutral-800 relative overflow-hidden group cursor-pointer hover:border-neutral-700 transition-all"
                >
                  <div className="absolute -top-12 -right-12 w-24 h-24 bg-red-600 rounded-full blur-[40px] opacity-20 group-hover:opacity-40 transition-opacity" />
                  <div className="relative z-10">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 shadow-lg ${effectivePlan === 'pro'
                        ? 'bg-purple-600 shadow-purple-900/40'
                        : 'bg-red-600 shadow-red-900/40'
                      }`}>
                      {effectivePlan === 'pro'
                        ? <Crown size={16} className="text-white" />
                        : <Zap size={16} className="text-white" />
                      }
                    </div>
                    <h3 className="text-xs font-black text-white mb-1 uppercase tracking-tight">
                      {effectivePlan === 'pro' ? 'Go Premium' : t('sidebar.upgradeToPro')}
                    </h3>
                    <p className="text-[9px] text-neutral-500 leading-tight mb-2">
                      {t('sidebar.upgradeDescription')}
                    </p>
                    <div className="flex items-center gap-1 text-[10px] font-black text-red-500 uppercase tracking-widest">
                      {t('sidebar.upgradeNow')} <ChevronRight size={10} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </>
      )}

      <div
        data-lenis-prevent
        className={`hidden md:flex flex-col transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-72'} bg-black border-r border-neutral-900 h-screen sticky top-0 relative overflow-x-hidden`}
      >
        <div className={isCollapsed ? 'px-2' : 'p-6'}>
          <div className="flex flex-col items-center pt-4 pb-2">
            <Logo size={isCollapsed ? 'sm' : 'lg'} centered />
          </div>

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="mx-auto flex items-center justify-center w-7 h-7 rounded-full bg-neutral-900 border border-neutral-700 hover:border-neutral-500 text-neutral-400 hover:text-white transition-all shadow-md mb-3"
          >
            {isCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          </button>

          {isCollapsed ? (
            <div className="flex flex-col gap-1.5 mb-4">
              <button
                onClick={handleNewTrade}
                data-tour="sidebar-newtrade"
                title="New CEX Trade"
                className="w-10 h-10 mx-auto flex items-center justify-center bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-lg active:scale-[0.98]"
              >
                <Plus size={18} className="text-white" />
              </button>
              <button
                onClick={handleNewDexTrade}
                title="New DEX Trade"
                className="w-10 h-10 mx-auto flex items-center justify-center bg-purple-600 hover:bg-purple-700 rounded-xl transition-all shadow-lg active:scale-[0.98]"
              >
                <Plus size={14} className="text-white" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2 mb-4">
              <button
                onClick={handleNewTrade}
                data-tour="sidebar-newtrade"
                className="flex-1 py-2 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] bg-red-600 hover:bg-red-700 shadow-red-900/20 text-sm"
              >
                <Plus size={16} />
                CEX
              </button>
              <button
                onClick={handleNewDexTrade}
                className="flex-1 py-2 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] bg-purple-600 hover:bg-purple-700 shadow-purple-900/20 text-sm"
              >
                <Plus size={16} />
                DEX
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col flex-1 overflow-y-auto">
          <div className={isCollapsed ? 'px-2' : 'px-6'}>
            <nav className="space-y-0.5" data-tour="sidebar-nav">
          {!isCollapsed && (
            <div className="pt-2 pb-1 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
              <LayoutDashboard size={10} className="text-neutral-400" /> Main Features
            </div>
          )}
          <NavItem icon={<BarChart size={16} />} label={t('sidebar.dashboard')} onClick={() => navigate('/dashboard')} active={currentPath === '/dashboard'} locked={effectivePlan === 'expired'} isCollapsed={isCollapsed} />
          <NavItem icon={<PieChart size={16} />} label={t('sidebar.reports')} onClick={() => navigate('/reports')} active={currentPath === '/reports'} locked={effectivePlan === 'expired'} isCollapsed={isCollapsed} />
          <NavItem icon={<TrendingUp size={16} />} label={t('sidebar.trades')} onClick={() => navigate('/trades')} active={currentPath === '/trades'} locked={effectivePlan === 'expired'} isCollapsed={isCollapsed} />
          <NavItem icon={<BookOpen size={16} />} label={t('sidebar.notebook')} onClick={() => navigate('/notebook')} active={currentPath === '/notebook'} locked={effectivePlan === 'expired'} isCollapsed={isCollapsed} />
          <div data-tour="sidebar-ai">
            <NavItem
              icon={<Bot size={16} />}
              label={t('sidebar.aiAssistant')}
              onClick={() => navigate('/ai-assistant')}
              active={currentPath === '/ai-assistant'}
              isCollapsed={isCollapsed}
            />
          </div>
          <NavItem
            icon={<Users size={16} />}
            label="Referral"
            onClick={() => navigate('/referral')}
            active={currentPath === '/referral'}
            isCollapsed={isCollapsed}
          />

          {!isCollapsed && (
            <div className="pt-2 pb-0.5 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
              <Crown size={10} className="text-purple-400" /> Premium Features
            </div>
          )}
          <NavItem icon={<Newspaper size={16} />} label="Crypto News" onClick={() => navigate('/news')} active={currentPath === '/news'} isCollapsed={isCollapsed} />
          <NavItem icon={<Sparkles size={16} />} label="Dev Picks" onClick={() => navigate('/dev-picks')} active={currentPath === '/dev-picks'} isCollapsed={isCollapsed} />
          <NavItem icon={<Compass size={16} />} label="Top 50 Trending" onClick={() => navigate('/trending')} active={currentPath === '/trending'} isCollapsed={isCollapsed} />
          <NavItem icon={<Trophy size={16} />} label="Top Traders" onClick={() => navigate('/top-traders')} active={currentPath === '/top-traders'} isCollapsed={isCollapsed} />

          <div className="py-2">
            <div className="h-px bg-neutral-900 w-full mb-2" />
            <NavItem icon={<LogOut size={16} />} label={t('sidebar.logout')} onClick={logout} isCollapsed={isCollapsed} />
          </div>
            </nav>
          </div>

          {!isCollapsed && (
            <div className="mt-auto p-3 space-y-2">
          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-xl border ${planCfg.bg} ${planCfg.border}`}>
            {PlanIcon && <PlanIcon size={12} className={planCfg.color} />}
            <div className="flex-1 min-w-0">
              <p className={`text-[10px] font-black uppercase tracking-widest ${planCfg.color}`}>
                {planCfg.label} Plan
              </p>
              {isTrial && subscription?.trial_ends_at && (
                <p className="text-[9px] text-neutral-500 truncate">
                  Trial sampai {new Date(subscription.trial_ends_at).toLocaleDateString('id-ID')}
                </p>
              )}
            </div>
          </div>

          {!isPremium && (
            <div
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-br from-neutral-900 to-neutral-950 p-3 rounded-3xl border border-neutral-800 relative overflow-hidden group cursor-pointer hover:border-neutral-700 transition-all"
            >
              <div className="absolute -top-12 -right-12 w-24 h-24 bg-red-600 rounded-full blur-[40px] opacity-20 group-hover:opacity-40 transition-opacity" />
              <div className="relative z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 shadow-lg ${effectivePlan === 'pro'
                    ? 'bg-purple-600 shadow-purple-900/40'
                    : 'bg-red-600 shadow-red-900/40'
                  }`}>
                  {effectivePlan === 'pro'
                    ? <Crown size={16} className="text-white" />
                    : <Zap size={16} className="text-white" />
                  }
                </div>
                <h3 className="text-xs font-black text-white mb-1 uppercase tracking-tight">
                  {effectivePlan === 'pro' ? 'Go Premium' : t('sidebar.upgradeToPro')}
                </h3>
                <p className="text-[9px] text-neutral-500 leading-tight mb-2">
                  {t('sidebar.upgradeDescription')}
                </p>
                <div className="flex items-center gap-1 text-[10px] font-black text-red-500 uppercase tracking-widest">
                  {t('sidebar.upgradeNow')} <ChevronRight size={10} />
                </div>
              </div>
            </div>
          )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  locked?: boolean;
  isCollapsed?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active = false, onClick, locked = false, isCollapsed = false }) => (
  <button
    onClick={locked ? undefined : onClick}
    title={isCollapsed ? label : (locked ? 'Upgrade plan untuk akses fitur ini' : undefined)}
    className={isCollapsed
      ? `w-10 h-10 mx-auto flex items-center justify-center rounded-xl transition-all ${locked
        ? 'text-neutral-700 cursor-not-allowed'
        : active
          ? 'bg-red-700/10 text-red-600 cursor-pointer'
          : 'text-neutral-500 hover:text-white hover:bg-neutral-900 cursor-pointer'
      }`
      : `w-full p-2 rounded-xl flex items-center gap-3 font-bold transition-all group ${locked
        ? 'text-neutral-700 cursor-not-allowed'
        : active
          ? 'bg-red-700/10 text-red-600 border-l-4 border-red-600 rounded-l-none pl-4 cursor-pointer'
          : 'text-neutral-500 hover:text-white hover:bg-neutral-900 cursor-pointer'
      }`
    }
  >
    <span className={`${locked ? 'text-neutral-700' : active ? 'text-red-600' : 'text-neutral-500 group-hover:text-red-500 transition-colors'}`}>
      {icon}
    </span>
    {!isCollapsed && <span className="text-xs tracking-tight flex-1 text-left">{label}</span>}
    {!isCollapsed && locked && <Lock size={12} className="text-neutral-700 flex-shrink-0" />}
  </button>
);

export default Sidebar;
